import React, { createContext, useContext, ReactElement, FC } from 'react';

import { decorate, observable } from 'mobx';
import { useLocalStore } from 'mobx-react-lite';

import { without } from 'lodash';
import { isNullOrUndefined } from 'util';

import {
  OnlineOrder,
  OrderItem,
  OrderingItem,
  ShopOrder,
  ShopInfo
} from 'kiolyn-api';

import { ViewState } from '../domain/models/ViewState';
import IShopRepository from '../domain/repositories/IShopRepository';
import newOrder from '../domain/usecases/NewOrder';
import newOrderItem from '../domain/usecases/NewOrderItem';
import updateOrderCalculatedValues from '../domain/usecases/UpdateOrderCalculatedValues';

export class ShopStore {
  // The main screen view state
  viewState = ViewState.OK;

  storeid: string;
  shopRepository?: IShopRepository;
  shopInfo: ShopInfo = {
    storeBalance: 0,
    items: []
  };
  order: OnlineOrder = newOrder();

  // Current being editted Order Item
  editingOrderItem?: OrderItem;

  submitState = ViewState.NONE;
  submitErrorMessage?: string;
  submitToken: string = '';

  constructor(
    storeid: string,
    shopRepository?: IShopRepository,
    shopInfo?: ShopInfo,
    order?: OnlineOrder,
    submitState?: ViewState
  ) {
    this.storeid = storeid;
    this.shopRepository = shopRepository;
    if (!isNullOrUndefined(shopInfo)) {
      this.shopInfo = shopInfo;
    }
    if (!isNullOrUndefined(order)) {
      this.order = order;
    }
    if (!isNullOrUndefined(submitState)) {
      this.submitState = submitState;
    }
  }

  // Reload the whole menu based on given storeid
  public async reload(): Promise<void> {
    if (isNullOrUndefined(this.shopRepository)) {
      return;
    }

    this.viewState = ViewState.LOADING;
    try {
      const shopInfo = await this.shopRepository.loadShopInfo(this.storeid);

      if (isNullOrUndefined(shopInfo)) {
        console.error('Could not load shop items.');
        this.viewState = ViewState.ERROR;
        return;
      }

      // Keep as local property
      this.shopInfo = shopInfo;

      // Create a new Order ready for putting item in
      this.order = newOrder({ id: this.storeid });
      this.viewState = ViewState.OK;
    } catch (e) {
      console.error(e);
      this.viewState = ViewState.ERROR;
    }
  }

  public isItemAvailable(item: OrderingItem): boolean {
    return (
      this.shopInfo.storeBalance -
        (this.order.total ?? 0) -
        (item.price ?? 0) >=
      0
    );
  }

  // Handling an item got selected
  public createItem(item: OrderingItem) {
    this.editingOrderItem = newOrderItem({ id: '' }, item);
  }

  public editItem(item: OrderItem) {
    this.editingOrderItem = item;
  }

  public deleteItem(item: OrderItem) {
    this.order.items = without(this.order.items, item);
    this.updateOrderAmounts();
  }

  /// Finish editing by clearing the editing order item
  /// and apply any changes plus recalculate every calculated values
  public finishEditing(update: boolean) {
    if (isNullOrUndefined(this.editingOrderItem)) {
      return;
    }
    if (update === true) {
      if (!this.order.items.includes(this.editingOrderItem)) {
        this.order.items.push(this.editingOrderItem);
      }
      this.updateOrderAmounts();
    }
    this.editingOrderItem = undefined;
  }

  public updateOrderAmounts() {
    updateOrderCalculatedValues(this.order, {});
  }

  public async submitCurrentOrder(): Promise<void> {
    try {
      if (isNullOrUndefined(this.shopRepository)) {
        throw new Error('Repository is not available');
      }

      this.submitErrorMessage = '';
      this.submitState = ViewState.LOADING;

      // Convert online order to shop order
      const shopOrder = {
        id: this.order.id,
        totalPoints: this.order.total ?? 0,
        items: this.order.items
      };

      const res = await this.shopRepository.submitOrder(
        this.storeid,
        shopOrder,
        this.submitToken
      );
      // If submit OK, then clear the current and create a new one ready for next ordering
      if (res.result === true) {
        this.order = newOrder({ id: this.storeid });
        this.shopInfo.storeBalance -= shopOrder.totalPoints;
        this.submitState = ViewState.OK;
      } else {
        this.submitErrorMessage = res.message;
        this.submitState = ViewState.ERROR;
      }
    } catch (e) {
      console.error(e);
      this.submitErrorMessage = 'Error submitting order, please retry later.';
      this.submitState = ViewState.ERROR;
    }
  }

  public async loadShopHistory(
    start: string,
    end: string
  ): Promise<ShopOrder[] | undefined> {
    return await this.shopRepository?.loadShopHistory(this.storeid, start, end);
  }
}

decorate(ShopStore, {
  viewState: observable,
  storeid: observable,
  order: observable,
  submitToken: observable,
  editingOrderItem: observable,
  submitState: observable,
  submitErrorMessage: observable
});

interface ShopProviderProps {
  children: ReactElement;
  storeid: string;
  shopRepository?: IShopRepository;
  shopInfo?: ShopInfo;
  order?: OnlineOrder;
  submitState?: ViewState;
}

export const ShopProvider: FC<ShopProviderProps> = ({
  children,
  storeid,
  shopRepository,
  shopInfo,
  order,
  submitState
}: ShopProviderProps) => {
  const shopStore = useLocalStore(
    () => new ShopStore(storeid, shopRepository, shopInfo, order, submitState)
  );
  shopStore.reload();
  return (
    <ShopContext.Provider value={shopStore}>{children}</ShopContext.Provider>
  );
};

export const ShopContext = createContext(new ShopStore(''));
export const useShop = () => useContext(ShopContext);
